// src/components/InvoicePipelineModal.jsx
// ============================================================================
// GUIDED PIPELINE: Review Invoice → Create Draft → Generate PDF → Email
// ============================================================================
// Triggered from TimelinePage when an invoiceDate or proforma date is set,
// or from a RADAR item / recommended action.
//
// Steps:
//   1. Review & confirm invoice details (amount, tax, office — editable)
//   2. Create draft + generate PDF on server
//   3. Download PDF + continue to email
//   4. Email handoff (opens EmailDraftModal or mailto with note about PDF)
//
// NOTE: Shared-folder archiving (SharePoint) is NOT part of this workflow.
// Archiving is a separate month-end finance/admin workflow on InvoicesPage.
// ============================================================================
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { X, Download, Mail, Check, FileText, Loader } from 'lucide-react';
import { downloadLocal, buildInvoiceFilename } from '../utils/invoiceStorageService';
import { getAdminBccEmail } from '../utils/emailTemplates';
import UsedInvoiceNumbersList from './UsedInvoiceNumbersList';
import {
  TAX_REGIME_OPTIONS, normalizeTaxRegime, getTaxPercentForRegime, officeForTaxRegime,
} from '../utils/invoiceTaxRegime';

// ── Suggest the next invoice number client-side ──
// Format: YYYYMMSS (8 digits) — month prefix + 2-digit sequence within month.
// Computed from already-issued numbers so it doesn't consume the RPC sequence;
// the user is free to overwrite it.
const computeSuggestedInvoiceNumber = (invoiceDate, allInvoices) => {
  if (!invoiceDate || !/^\d{4}-\d{2}/.test(invoiceDate)) return '';
  const monthPrefix = invoiceDate.slice(0, 7).replace('-', ''); // "2026-02" -> "202602"
  const list = Array.isArray(allInvoices) ? allInvoices : [];
  let maxSeq = 0;
  for (const inv of list) {
    const num = String(inv?.invoiceNumber || '').trim();
    if (num.length === 8 && num.startsWith(monthPrefix)) {
      const seq = Number(num.slice(6));
      if (Number.isFinite(seq) && seq > maxSeq) maxSeq = seq;
    }
  }
  return `${monthPrefix}${String(maxSeq + 1).padStart(2, '0')}`;
};

// ── Step constants ──
const STEP_REVIEW = 'review';
const STEP_GENERATING = 'generating';
const STEP_PDF_READY = 'pdf_ready';
const STEP_DONE = 'done';

// ── Tax helpers (mirrored from InvoicesPage for consistency) ──
const DEFAULT_GST_PERCENT = 18;
const DEFAULT_TDS_PERCENT = 10;

// TAX_REGIME_OPTIONS and getTaxPercentForRegime come from utils/invoiceTaxRegime.

const computeAmounts = (base, gstPct, tdsPct, applyTds = true) => {
  const b = Number(base) || 0;
  const g = Number(gstPct) || 0;
  const t = Number(tdsPct) || 0;
  const gross = b + (b * g / 100);
  const tds = applyTds ? (b * t / 100) : 0;
  const net = gross - tds;
  return { gross, tds, net };
};

/**
 * InvoicePipelineModal
 *
 * @param {object} props
 * @param {object} props.project - Current project
 * @param {object} [props.client] - The project's client; its default tax regime pre-fills the form
 * @param {string} props.stageKey - Stage key for the invoice
 * @param {string} props.stageName - Display name of the stage
 * @param {string} props.invoiceDate - Date for the invoice (YYYY-MM-DD)
 * @param {string} props.invoiceDocType - 'tax' | 'proforma'
 * @param {object} props.invoicesHook - The useSupabaseInvoices hook instance
 * @param {function} props.fmtCurr - Currency formatter
 * @param {function} props.onClose - Called when modal closes
 * @param {function} props.onComplete - Called after pipeline completes with { invoiceId, downloaded, emailed }
 * @param {function} [props.onOpenEmail] - Called when user wants to email; receives { invoiceId, pdfBlob, pdfFilename }
 */
export default function InvoicePipelineModal({
  project,
  client,
  stageKey,
  stageName,
  invoiceDate,
  invoiceDocType: initialDocType = 'tax',
  invoicesHook,
  projectsById,
  fmtCurr,
  onClose,
  onComplete,
  onOpenEmail,
}) {
  const [step, setStep] = useState(STEP_REVIEW);
  const [error, setError] = useState('');
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('invoice.pdf');
  const [downloaded, setDownloaded] = useState(false);
  const blobRef = useRef(null);
  const pdfUrlRef = useRef(null);

  // ── Derive defaults: the client's tax regime, else the project's currency ──
  // The regime decides the office (GST from Mumbai, VAT from Rotterdam);
  // 'Other' leaves the office to the project's currency.
  const isRotterdam = project?.contractCurrency === 'EUR';
  const defaultTaxRegime = normalizeTaxRegime(client?.defaultTaxRegime) ?? (isRotterdam ? 'NL' : 'IN_inter_state');
  const defaultOffice = officeForTaxRegime(defaultTaxRegime) ?? (isRotterdam ? 'NL' : 'IN');
  const defaultGst = getTaxPercentForRegime(defaultTaxRegime);
  const defaultTds = defaultOffice === 'NL' ? 0 : DEFAULT_TDS_PERCENT;

  // ── Form state (editable in review step) ──
  const [form, setForm] = useState({
    docType: initialDocType,
    officeLocation: defaultOffice,
    taxRegime: defaultTaxRegime,
    gstPercent: defaultGst,
    tdsPercent: defaultTds,
    applyTds: defaultOffice !== 'NL',
    customTaxPercent: 18,
    customStageName: '',
    manualOverride: false,
    baseAmount: '',
    // Manual entry by default — pre-filled with a client-side suggestion
    // computed from existing invoice numbers in the same YYYYMM window.
    invoiceNumber: '',
    invoiceNumberEdited: false,
  });

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  // Pull all invoices for the suggestion + used-numbers list. Idempotent: the
  // hook caches results in state, so opening the modal again is cheap.
  useEffect(() => {
    if (invoicesHook?.fetchAllInvoices) {
      invoicesHook.fetchAllInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suggested next number for the current invoice date (or today if blank).
  const effectiveInvoiceDate = invoiceDate || new Date().toISOString().slice(0, 10);
  const suggestedInvoiceNumber = useMemo(
    () => computeSuggestedInvoiceNumber(effectiveInvoiceDate, invoicesHook?.allInvoices),
    [effectiveInvoiceDate, invoicesHook?.allInvoices]
  );

  // Pre-fill / refresh the suggested number until the user edits the field.
  useEffect(() => {
    if (form.invoiceNumberEdited) return;
    if (suggestedInvoiceNumber && form.invoiceNumber !== suggestedInvoiceNumber) {
      setForm(prev => prev.invoiceNumberEdited ? prev : { ...prev, invoiceNumber: suggestedInvoiceNumber });
    }
  }, [suggestedInvoiceNumber, form.invoiceNumberEdited, form.invoiceNumber]);

  // Duplicate detection: warn (don't block) if the chosen number already exists.
  const duplicateExists = useMemo(() => {
    const trimmed = String(form.invoiceNumber || '').trim();
    if (!trimmed) return false;
    return (invoicesHook?.allInvoices || []).some(
      inv => String(inv?.invoiceNumber || '').trim() === trimmed
    );
  }, [form.invoiceNumber, invoicesHook?.allInvoices]);

  // ── Suggested base amount from stage timeline ──
  const suggestedBaseAmount = useMemo(() => {
    if (!project) return 0;
    const sk = stageKey || '';
    const baseFee = Number(project.budget) || 0;

    // CC monthly
    if (sk.startsWith('cc__') && sk !== 'cc') {
      const billing = project.stageTimeline?.[sk]?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) return lumpsum;
      return Number(project.retainerConfig?.monthlyAmount) || 0;
    }

    // Misc scope
    if (sk.startsWith('misc__') && !sk.includes('__part__')) {
      const scopeId = sk.replace('misc__', '');
      const scope = (Array.isArray(project.miscFeeScopes) ? project.miscFeeScopes : [])
        .find(s => s.id === scopeId);
      const billing = project.stageTimeline?.[sk]?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) return lumpsum;
      return Number(scope?.amount) || 0;
    }

    // Part stage
    const partIdx = sk.indexOf('__part__');
    if (partIdx > -1) {
      const parentKey = sk.slice(0, partIdx);
      const partId = sk.slice(partIdx + '__part__'.length);
      const partStages = project.stageTimeline?.[parentKey]?.partStages;
      const part = Array.isArray(partStages)
        ? partStages.find(ps => String(ps.id) === String(partId))
        : null;
      const billing = part?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) return lumpsum;
      const parentBilling = project.stageTimeline?.[parentKey]?.billing || {};
      const parentLumpsum = Number(parentBilling.lumpsum ?? parentBilling.lumpSum ?? 0);
      const parentPlanned = parentLumpsum > 0
        ? parentLumpsum
        : baseFee * (Number(parentBilling.percent) || 0) / 100;
      const pctOfStage = Number(billing.percentOfStage) || 0;
      return parentPlanned * pctOfStage / 100;
    }

    // Full stage
    const billing = project.stageTimeline?.[sk]?.billing || {};
    const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
    if (lumpsum > 0) return lumpsum;
    const percent = Number(billing.percent) || 0;
    return baseFee * percent / 100;
  }, [stageKey, project]);

  const effectiveBase = form.manualOverride ? (Number(form.baseAmount) || 0) : suggestedBaseAmount;
  const { gross, tds, net } = computeAmounts(effectiveBase, form.gstPercent, form.tdsPercent, form.applyTds);

  // Format in contract currency
  const fmtCC = (a) => fmtCurr ? fmtCurr(a) : String(a);

  // Tax regime change handler
  // The regime moves the office (and TDS with it); the Office radio below can
  // still override for the odd case the two part ways.
  const handleTaxRegimeChange = (newRegime) => {
    const taxPct = getTaxPercentForRegime(newRegime, form.customTaxPercent);
    setForm(prev => {
      const office = officeForTaxRegime(newRegime) ?? prev.officeLocation;
      const officeChanged = office !== prev.officeLocation;
      return {
        ...prev, taxRegime: newRegime, gstPercent: taxPct, officeLocation: office,
        ...(officeChanged ? { tdsPercent: office === 'NL' ? 0 : DEFAULT_TDS_PERCENT, applyTds: office !== 'NL' } : {}),
      };
    });
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    };
  }, []);

  // ── Create invoice + generate PDF ──
  const handleCreateAndGenerate = useCallback(async () => {
    if (!invoicesHook?.createInvoice || !project) {
      setError('Invoice system not available.');
      return;
    }
    if (effectiveBase <= 0) {
      setError('Base amount must be greater than 0.');
      return;
    }
    const trimmedInvoiceNumber = String(form.invoiceNumber || '').trim();
    if (!trimmedInvoiceNumber) {
      setError('Invoice number is required.');
      return;
    }
    if (duplicateExists) {
      const proceed = window.confirm(
        `Invoice number "${trimmedInvoiceNumber}" is already in use.\n\n` +
        `You can still proceed, but you will need to resolve the conflict ` +
        `after assignment (e.g. update one of the invoices to a unique number).`
      );
      if (!proceed) return;
    }
    setStep(STEP_GENERATING);
    setError('');

    try {
      const finalInvoiceNumber = trimmedInvoiceNumber;

      // 2. Create invoice draft with reviewed values
      const createResult = await invoicesHook.createInvoice({
        projectId: project.id,
        stageKey: stageKey || null,
        customStageName: form.customStageName.trim() || null,
        invoiceDate: effectiveInvoiceDate,
        invoiceNumber: finalInvoiceNumber,
        invoiceDocType: form.docType,
        officeLocation: form.officeLocation,
        currency: form.officeLocation === 'NL' ? 'EUR' : 'INR',
        baseAmount: effectiveBase,
        gstPercent: Number(form.gstPercent) || 0,
        tdsPercent: Number(form.tdsPercent) || 0,
        applyTds: form.applyTds,
        taxRegime: form.taxRegime,
        isManualOverride: form.manualOverride || form.invoiceNumberEdited,
      });

      if (!createResult.success) {
        setError(createResult.error || 'Failed to create invoice.');
        setStep(STEP_REVIEW);
        return;
      }

      const inv = createResult.data;
      setCreatedInvoice(inv);

      // 3. Generate PDF
      const officeKey = form.officeLocation === 'NL' ? 'rotterdam' : 'mumbai';
      const { supabase } = await import('../supabaseClient');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || '';

      const response = await fetch('/api/invoice-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          invoiceId: inv.id,
          officeKey,
          invoiceDocType: form.docType,
        }),
      });

      if (!response.ok) {
        let errMsg = `PDF generation failed (${response.status})`;
        try {
          const errJson = await response.json();
          errMsg = errJson.error || errMsg;
        } catch { /* ignore */ }
        setError(errMsg);
        setStep(STEP_PDF_READY);
        return;
      }

      // Build filename
      let filename = buildInvoiceFilename({
        officeKey,
        invoiceDocType: form.docType,
        invoiceNumber: inv.invoiceNumber,
        projectName: project?.name,
      });
      const disposition = response.headers.get('Content-Disposition');
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const blob = await response.blob();
      blobRef.current = blob;
      setPdfFilename(filename);

      const url = URL.createObjectURL(blob);
      pdfUrlRef.current = url;
      setPdfUrl(url);
      setStep(STEP_PDF_READY);

    } catch (err) {
      setError(err?.message || 'An unexpected error occurred.');
      setStep(STEP_REVIEW);
    }
  }, [invoicesHook, project, stageKey, form, effectiveBase, effectiveInvoiceDate, duplicateExists]);

  // ── Download PDF ──
  const handleDownload = () => {
    if (blobRef.current) {
      downloadLocal(blobRef.current, pdfFilename);
      setDownloaded(true);
    }
  };

  // ── Continue to email ──
  const handleContinueToEmail = () => {
    if (onOpenEmail && createdInvoice) {
      onOpenEmail({
        invoiceId: createdInvoice.id,
        invoiceNumber: createdInvoice.invoiceNumber,
        invoiceNetAmount: createdInvoice.netAmount ?? null,
        pdfBlob: blobRef.current,
        pdfFilename,
        bccAdmin: getAdminBccEmail(),
        invoiceDocType: form.docType,
      });
      return;
    }
    setStep(STEP_DONE);
    onComplete?.({ invoiceId: createdInvoice?.id, downloaded, emailed: false });
    onClose();
  };

  const handleDownloadAndEmail = () => {
    handleDownload();
    setTimeout(() => handleContinueToEmail(), 150);
  };

  const handleFinish = () => {
    onComplete?.({ invoiceId: createdInvoice?.id, downloaded, emailed: false });
    onClose();
  };

  // ── Styles ──
  const modalBg = {
    position: 'fixed', inset: 0, zIndex: 1100,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const modalBox = {
    background: 'var(--brio-white, #fff)',
    border: '1px solid var(--brio-border)',
    width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto',
    fontFamily: 'var(--brio-font-family, Raleway, sans-serif)',
  };
  const inputStyle = {
    padding: '5px 8px', fontSize: '11px', width: '100%',
    border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)',
  };

  return (
    <div style={modalBg} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={modalBox}>
        {/* Header */}
        <div className="brio-flex brio-justify-between brio-items-center"
          style={{ padding: '12px 16px', borderBottom: '1px solid var(--brio-border)' }}>
          <span className="brio-font-bold" style={{ fontSize: '13px' }}>
            {step === STEP_REVIEW && 'Review & Create Invoice'}
            {step === STEP_GENERATING && 'Creating Invoice & Generating PDF…'}
            {step === STEP_PDF_READY && 'Invoice PDF Ready'}
            {step === STEP_DONE && 'Done'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* ── Step: Review ── */}
          {step === STEP_REVIEW && (
            <>
              {/* Context banner */}
              <div style={{ padding: '8px 10px', background: 'var(--brio-grey-light)', border: '1px solid var(--brio-border)', fontSize: '11px' }}>
                <div><strong>{project?.name || 'Project'}</strong> — {stageName || stageKey || 'Stage'}</div>
                <div className="brio-text-grey" style={{ marginTop: '2px' }}>
                  Invoice date: {invoiceDate || new Date().toISOString().slice(0, 10)}
                </div>
              </div>

              {/* Document type + Office */}
              <div className="brio-flex brio-gap-s">
                <div style={{ flex: 1 }}>
                  <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Document Type</label>
                  <div className="brio-flex brio-gap-s">
                    {[{ key: 'tax', label: 'Tax' }, { key: 'proforma', label: 'Proforma' }].map(({ key, label }) => (
                      <label key={key} className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer', fontSize: '11px' }}>
                        <input type="radio" name="pipelineDocType" value={key} checked={form.docType === key}
                          onChange={() => set('docType', key)} style={{ accentColor: 'var(--brio-gold, #9F902A)' }} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Office</label>
                  <div className="brio-flex brio-gap-s">
                    {[{ key: 'NL', label: 'Rotterdam' }, { key: 'IN', label: 'Mumbai' }].map(({ key, label }) => (
                      <label key={key} className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer', fontSize: '11px' }}>
                        <input type="radio" name="pipelineOffice" value={key} checked={form.officeLocation === key}
                          onChange={() => setForm(prev => ({
                            ...prev,
                            officeLocation: key,
                            tdsPercent: key === 'NL' ? 0 : DEFAULT_TDS_PERCENT,
                            applyTds: key !== 'NL',
                          }))}
                          style={{ accentColor: 'var(--brio-gold, #9F902A)' }} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Custom stage name */}
              <div>
                <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Custom Stage Name (for PDF)</label>
                <input type="text" value={form.customStageName} onChange={e => set('customStageName', e.target.value)}
                  placeholder={stageName || stageKey || 'Leave blank to use default'}
                  style={{ ...inputStyle }} />
              </div>

              {/* Invoice Number — manual entry, pre-filled with suggestion */}
              <div>
                <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>
                  Invoice Number
                </label>
                <input type="text" value={form.invoiceNumber}
                  onChange={e => setForm(prev => ({ ...prev, invoiceNumber: e.target.value, invoiceNumberEdited: true }))}
                  placeholder={suggestedInvoiceNumber || 'YYYYMMSS'}
                  className="brio-input"
                  style={inputStyle} />
                <div className="brio-text-xs" style={{
                  marginTop: '2px',
                  color: duplicateExists ? 'var(--brio-amber, #b45309)' : 'var(--brio-grey)',
                }}>
                  {duplicateExists
                    ? `⚠ "${form.invoiceNumber}" is already in use — please resolve this conflict after assignment.`
                    : suggestedInvoiceNumber
                      ? `Suggested next: ${suggestedInvoiceNumber} (edit freely; format YYYYMMSS)`
                      : 'Manual — ensure uniqueness (format YYYYMMSS)'}
                </div>
                <UsedInvoiceNumbersList
                  allInvoices={invoicesHook?.allInvoices}
                  projectsById={projectsById}
                  filterDate={effectiveInvoiceDate}
                  currentNumber={form.invoiceNumber}
                />
              </div>

              {/* Base Amount */}
              <div>
                <div className="brio-flex brio-justify-between brio-items-center" style={{ marginBottom: '4px' }}>
                  <label className="brio-text-xs brio-font-medium">Base Amount</label>
                  <label className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer' }}>
                    <span className="brio-text-xs brio-text-grey">Manual override</span>
                    <input type="checkbox" checked={form.manualOverride}
                      onChange={() => {
                        if (form.manualOverride) {
                          setForm(prev => ({ ...prev, manualOverride: false, baseAmount: '' }));
                        } else {
                          setForm(prev => ({ ...prev, manualOverride: true, baseAmount: suggestedBaseAmount || '' }));
                        }
                      }}
                      style={{ cursor: 'pointer', width: '13px', height: '13px' }} />
                  </label>
                </div>
                <input type="number" min="0" step="0.01"
                  value={form.manualOverride ? form.baseAmount : (suggestedBaseAmount || '')}
                  onChange={e => set('baseAmount', e.target.value)}
                  disabled={!form.manualOverride}
                  style={{ ...inputStyle, backgroundColor: !form.manualOverride ? 'var(--brio-grey-light)' : undefined }} />
                <div className="brio-text-xs brio-text-grey" style={{ marginTop: '2px' }}>
                  {form.manualOverride
                    ? 'Manual override enabled'
                    : suggestedBaseAmount > 0
                      ? `Auto-calculated from stage timeline: ${fmtCC(suggestedBaseAmount)}`
                      : 'No stage billing data found — enable manual override'}
                </div>
              </div>

              {/* Tax Regime + TDS */}
              <div className="brio-flex brio-gap-s">
                <div style={{ flex: 2 }}>
                  <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Tax Regime</label>
                  <select value={form.taxRegime} onChange={e => handleTaxRegimeChange(e.target.value)} style={inputStyle}>
                    {TAX_REGIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {form.taxRegime === 'Other' ? (
                  <div style={{ flex: 1 }}>
                    <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Custom Tax %</label>
                    <input type="number" min="0" max="100" step="0.01" value={form.customTaxPercent}
                      onChange={e => {
                        const pct = Number(e.target.value) || 0;
                        setForm(prev => ({ ...prev, customTaxPercent: pct, gstPercent: pct }));
                      }} style={inputStyle} />
                  </div>
                ) : (
                  <div style={{ flex: 1 }}>
                    <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Tax %</label>
                    <input type="number" value={form.gstPercent} disabled
                      style={{ ...inputStyle, backgroundColor: 'var(--brio-grey-light)' }} />
                  </div>
                )}
              </div>

              {/* TDS */}
              <div className="brio-flex brio-gap-s brio-items-end">
                <div style={{ flex: 1 }}>
                  <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>TDS %</label>
                  <input type="number" min="0" max="100" step="0.01" value={form.tdsPercent}
                    onChange={e => set('tdsPercent', e.target.value)} disabled={!form.applyTds}
                    style={{ ...inputStyle, opacity: !form.applyTds ? 0.5 : 1 }} />
                </div>
                <div style={{ flex: 1, paddingBottom: '2px' }}>
                  <label className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.applyTds} onChange={e => set('applyTds', e.target.checked)}
                      style={{ cursor: 'pointer', width: '13px', height: '13px' }} />
                    <span className="brio-text-xs brio-font-medium">Apply TDS</span>
                  </label>
                  <div className="brio-text-xs brio-text-grey" style={{ marginTop: '1px' }}>
                    {form.applyTds ? `TDS ${Number(form.tdsPercent) || 0}% deducted on base` : 'TDS not applied'}
                  </div>
                </div>
              </div>

              {/* Computed Totals */}
              <div style={{ padding: '10px 12px', background: 'var(--brio-grey-light)', border: '1px solid var(--brio-border)' }}>
                <div className="brio-text-xs brio-font-medium" style={{ marginBottom: '6px' }}>Computed Totals</div>
                <div className="brio-flex brio-justify-between brio-text-s">
                  <span className="brio-text-grey">Base Amount</span>
                  <span className="brio-font-medium">{fmtCC(effectiveBase)}</span>
                </div>
                {form.taxRegime === 'IN_intra_state' ? (
                  <>
                    <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}>
                      <span className="brio-text-grey">+ CGST (9%)</span><span>{fmtCC(effectiveBase * 9 / 100)}</span>
                    </div>
                    <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}>
                      <span className="brio-text-grey">+ SGST (9%)</span><span>{fmtCC(effectiveBase * 9 / 100)}</span>
                    </div>
                  </>
                ) : (
                  <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}>
                    <span className="brio-text-grey">+ {form.taxRegime === 'NL' ? 'VAT' : form.taxRegime === 'IN_inter_state' ? 'IGST' : 'Tax'} ({Number(form.gstPercent) || 0}%)</span>
                    <span>{fmtCC(gross - effectiveBase)}</span>
                  </div>
                )}
                <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}>
                  <span className="brio-text-grey">Gross Amount</span><span className="brio-font-medium">{fmtCC(gross)}</span>
                </div>
                <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}>
                  <span className="brio-text-grey">{form.applyTds ? `− TDS (${Number(form.tdsPercent) || 0}% on base)` : '− TDS (not applied)'}</span>
                  <span style={{ color: form.applyTds && tds > 0 ? 'var(--brio-red)' : 'var(--brio-grey)' }}>
                    {form.applyTds ? `−${fmtCC(tds)}` : '—'}
                  </span>
                </div>
                <div className="brio-flex brio-justify-between brio-text-s brio-font-bold"
                  style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid var(--brio-border)' }}>
                  <span>Net Receivable</span><span>{fmtCC(net)}</span>
                </div>
              </div>

              {error && <div className="brio-text-xs" style={{ color: 'var(--brio-red)' }}>{error}</div>}
            </>
          )}

          {/* ── Step: Generating ── */}
          {step === STEP_GENERATING && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Loader className="w-6 h-6" style={{ margin: '0 auto 12px', animation: 'spin 1s linear infinite', color: 'var(--brio-gold)' }} />
              <div className="brio-text-s" style={{ color: 'var(--brio-grey)' }}>
                Creating invoice and generating PDF…
              </div>
              <div className="brio-text-xs brio-text-grey" style={{ marginTop: '4px' }}>
                This may take a few seconds.
              </div>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* ── Step: PDF Ready ── */}
          {step === STEP_PDF_READY && (
            <>
              {createdInvoice && (
                <div style={{ padding: '8px 10px', background: '#f0fdf4', border: '1px solid #86efac', fontSize: '11px' }}>
                  <div className="brio-flex brio-items-center brio-gap-xs">
                    <Check className="w-3 h-3" style={{ color: '#16a34a' }} />
                    <span>
                      <strong>{form.docType === 'proforma' ? 'Proforma' : 'Tax'} Invoice</strong> created
                      {createdInvoice.invoiceNumber && <> — #{createdInvoice.invoiceNumber}</>}
                      {' · '}{fmtCC(net)} net
                    </span>
                  </div>
                </div>
              )}

              {pdfUrl ? (
                <div style={{ border: '1px solid var(--brio-border)', background: '#f9f9f9' }}>
                  <iframe src={pdfUrl} title="Invoice PDF preview"
                    style={{ width: '100%', height: '300px', border: 'none' }} />
                </div>
              ) : error ? (
                <div className="brio-text-xs" style={{ color: 'var(--brio-red)', padding: '12px 0' }}>
                  <FileText className="w-4 h-4" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                  PDF could not be generated: {error}
                  <div className="brio-text-grey" style={{ marginTop: '4px' }}>
                    The invoice draft was saved. You can generate the PDF from the Invoices page.
                  </div>
                </div>
              ) : null}

              {pdfUrl && (
                <div className="brio-flex brio-flex-col brio-gap-xs">
                  <button onClick={handleDownloadAndEmail} className="brio-btn brio-btn-primary brio-flex brio-items-center brio-gap-xs"
                    style={{ padding: '8px 14px', fontSize: '11px', justifyContent: 'flex-start' }}>
                    <Mail className="w-3 h-3" /> Download PDF + Continue to Email
                  </button>
                  <button onClick={handleDownload} className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                    style={{ padding: '8px 14px', fontSize: '11px', justifyContent: 'flex-start' }}>
                    <Download className="w-3 h-3" /> {downloaded ? 'Download PDF again' : 'Download PDF only'}
                  </button>
                  <button onClick={handleContinueToEmail} className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                    style={{ padding: '8px 14px', fontSize: '11px', justifyContent: 'flex-start' }}>
                    <Mail className="w-3 h-3" /> Continue to Email (without download)
                  </button>
                </div>
              )}

              <div className="brio-text-xs brio-text-grey" style={{ fontStyle: 'italic', marginTop: '4px' }}>
                {downloaded
                  ? 'PDF downloaded — attach it manually in your email app.'
                  : 'Your email app cannot attach files automatically. After downloading, attach the PDF manually.'}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="brio-flex brio-justify-end brio-gap-xs"
          style={{ padding: '12px 16px', borderTop: '1px solid var(--brio-border)' }}>
          {step === STEP_REVIEW && (
            <>
              <button onClick={onClose} className="brio-btn brio-btn-secondary" style={{ padding: '6px 14px', fontSize: '11px' }}>
                Cancel
              </button>
              <button onClick={handleCreateAndGenerate} className="brio-btn brio-btn-primary" style={{ padding: '6px 14px', fontSize: '11px' }}>
                Create Invoice + Generate PDF
              </button>
            </>
          )}
          {step === STEP_PDF_READY && (
            <button onClick={handleFinish} className="brio-btn brio-btn-secondary" style={{ padding: '6px 14px', fontSize: '11px' }}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
